import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type AwardCriterionDto,
  AwardCriterionType,
  type AwardSubCriterionDto,
  type LibraryElementDto,
  LibraryElementTypeDto,
  useCreateLibraryElementMutation,
  useListLibraryElementsQuery,
  useUpdateLibraryElementMutation,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { useApiError } from "../errors/useApiError";
import {
  criterionTypeLabelKey,
  criterionTypeSelectValue,
} from "../../pages/sourcing-events/buyer/wizard/utils/publicProcurementOptions";
import { awardCriterionToLibraryContent, toLibrarySubCriterion } from "./awardLibraryContent";
import { FormOptionRow } from "./fields/FormSectionBand";

// t('Add it under {{criterion}} in the library')
// t('The criterion is already in the library — the requirement is added under it, weighted {{weight}} %')
// t('The criterion is not in the library yet — it is created there with this requirement, weighted {{weight}} %')
// t('The criterion is not in the library yet — it is created there with this requirement')
// t('The criterion is already in the library — the requirement is added under it')

const nameKey = (name: string | null | undefined) => (name ?? "").trim().toLowerCase();

/**
 * "Keep this requirement under its criterion, in the library."
 *
 * The sibling of `useSaveToLibraryOption`, and deliberately a different destination. A requirement kept
 * on its own is reusable under any criterion of its kind but carries no weight — alone it has no
 * siblings to be a share of. Kept *inside* a criterion it is part of a set whose shares total 100, and
 * the whole criterion becomes reusable with its requirements already weighted.
 *
 * The two are independent: either, both, or neither.
 */
export const useKeepUnderCriterionOption = (
  criterion: AwardCriterionDto | null | undefined,
) => {
  const { t } = useTranslation();
  const { triggerSoft } = useApiError();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const canWriteToLibrary = selectedOrg.canEditDocumentTemplates && selectedOrg.subscription?.hasBuyerAccess !== false;
  const [isChecked, setIsChecked] = useState(false);
  const [createLibraryElement] = useCreateLibraryElementMutation();
  const [updateLibraryElement] = useUpdateLibraryElementMutation();
  /**
   * Criteria this hook created, until the list catches up.
   *
   * The lookup below reads the fetched list, which the create only invalidates once it lands. Two
   * requirements kept under the same not-yet-stored criterion in quick succession would otherwise
   * create the criterion twice, and the second would say "not in the library yet" about something it
   * had just put there.
   */
  const created = useRef<Record<string, LibraryElementDto>>({});
  const { data } = useListLibraryElementsQuery({}, { skip: !canWriteToLibrary });

  // Only the organisation's own criteria are ever written to. A global SourceMagnet standard by the
  // same name is left alone — the BFF answers a write to one with 403 unless you are staff — so an
  // organisation that has only the standard gets a criterion of its own instead.
  const existing: LibraryElementDto | undefined = useMemo(() => {
    const wanted = nameKey(criterion?.name);
    if (wanted === "") return undefined;
    const fetched = (data?.elements ?? []).find(
      (element) =>
        element.content.type === LibraryElementTypeDto.Award &&
        !element.owner.global &&
        nameKey(element.content.award?.name) === wanted &&
        // Name alone is not enough: a criterion filed under a different type would take a requirement
        // whose weight is a share of a set it does not belong to. Environment sub-types collapse to
        // one family, the same way the type picker treats them.
        element.content.award != null &&
        criterionTypeSelectValue(element.content.award.type) ===
          criterionTypeSelectValue(criterion?.type ?? AwardCriterionType.Quality)
    );
    return fetched ?? created.current[wanted];
  }, [data?.elements, criterion?.name, criterion?.type]);

  const append = useCallback(
    async (sub: Omit<AwardSubCriterionDto, "id">) => {
      const written = toLibrarySubCriterion(sub);
      try {
        if (existing?.content.award) {
          const award = existing.content.award;
          // A requirement already there under the same name is replaced rather than duplicated: the
          // criterion is one definition, and two rows with one name is not a version of it.
          const kept = award.subCriteria ?? [];
          const at = kept.findIndex((s) => nameKey(s.name) === nameKey(written.name));
          const subCriteria =
            at === -1 ? [...kept, written] : kept.map((s, i) => (i === at ? written : s));
          await updateLibraryElement({
            updateLibraryElementRequest: {
              id: existing.id,
              // Keeping the criterion's own id is what makes this an update rather than a new element
              // wearing the same name — the same rule the library page's own writes follow.
              content: {
                type: LibraryElementTypeDto.Award,
                award: { ...award, subCriteria },
              },
              tags: existing.tags,
            },
          }).unwrap();
          return;
        }
        if (!criterion) return;
        // Nothing to add to yet, so the criterion itself is what is kept — carrying this requirement
        // alone. The others it may hold in the procurement were not what the buyer picked here.
        const element = await createLibraryElement({
          createLibraryElementRequest: {
            content: awardCriterionToLibraryContent({ ...criterion, subCriteria: [written] }),
            global: false,
          },
        }).unwrap();
        created.current[nameKey(criterion.name)] = element;
      } catch (e) {
        triggerSoft(e);
      }
    },
    [existing, criterion, updateLibraryElement, createLibraryElement, triggerSoft]
  );

  /** Writes only if the option was checked. Trails the write that put the requirement in the criterion. */
  const appendIfChosen = useCallback(
    async (sub: Omit<AwardSubCriterionDto, "id">) => {
      if (!canWriteToLibrary || !isChecked) return;
      await append(sub);
    },
    [canWriteToLibrary, isChecked, append]
  );

  /** A fresh form starts from the default again — the last visit's answer is not this visit's intent. */
  const reset = useCallback(() => setIsChecked(false), []);

  // The hint says which of the two writes this is, because "added under it" and "created there" are
  // materially different outcomes and the buyer is choosing between them blind otherwise.
  const hint = (weight: number | null | undefined) => {
    if (weight == null) {
      return existing
        ? t("The criterion is already in the library — the requirement is added under it")
        : t("The criterion is not in the library yet — it is created there with this requirement");
    }
    return existing
      ? t("The criterion is already in the library — the requirement is added under it, weighted {{weight}} %", {
          weight,
        })
      : t("The criterion is not in the library yet — it is created there with this requirement, weighted {{weight}} %", {
          weight,
        });
  };

  /**
   * The row, built where the weight is known. The weight is typed in the form rather than held here, so
   * the caller passes what it currently reads — the hint then names the share that will be stored.
   */
  const optionFor = (weight: number | null | undefined) =>
    canWriteToLibrary && criterion ? (
      <FormOptionRow
        showDivider={false}
        isChecked={isChecked}
        onChange={setIsChecked}
        title={t("Add it under {{criterion}} in the library", {
          criterion: criterion.name?.trim() || t(criterionTypeLabelKey(criterion.type)),
        })}
        hint={hint(weight)}
      />
    ) : null;

  return { canWriteToLibrary, optionFor, isChecked, reset, appendIfChosen, isCriterionInLibrary: !!existing };
};
